import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { ContractDetailStatus, FeatureFlagUseCase, ProductFamily } from '@ibk/shared-types';
import { useActiveContractProducts } from '../../../hooks/useActiveContractProducts';
import { useBenefitContract } from '../../../hooks/useBenefitContract';
import { benefitApi } from '../../../services/benefitApi';
import { useCreateEmissorBenefitContractByDomain } from '../../../services/emissorBenefitsApi';
export function useHiring() {
    const navigate = useNavigate();
    const { isFeatureFlagWhiteListed, isLoading: isLoadingFeatureFlags } = useFeatureFlag();
    const isPatEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_ENABLE_PAT);
    const { contractData: existingContract, hasContract, isLoading: isLoadingContract } = useBenefitContract();
    const { products: contractProducts, isLoading: isLoadingProducts } = useActiveContractProducts({
        voucherContractId: existingContract?.id ?? null,
    });
    const isEmissorFlow = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_EMISSOR_ENABLED) && !hasContract;
    const [createContract, { isLoading: isCreating }] = benefitApi.useCreateContractMutation();
    const [createEmissorBenefitContract, { isLoading: isCreatingEmissor }] = useCreateEmissorBenefitContractByDomain();
    const [addProductFamiliesToContract, { isLoading: isAddingFamilies }] = benefitApi.useAddProductFamiliesToContractMutation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requestRef = useRef(null);
    const isLoadingContractData = isLoadingContract || isLoadingProducts;
    const contractedFamilies = useMemo(() => {
        if (!hasContract || !contractProducts || contractProducts.length === 0) {
            return [];
        }
        const ACTIVE_STATUSES = [ContractDetailStatus.ACTIVE, ContractDetailStatus.PENDING];
        const families = new Set();
        contractProducts.forEach(voucher => {
            const status = voucher.status;
            const isActiveOrPending = status && ACTIVE_STATUSES.includes(status);
            if (!isActiveOrPending) {
                return;
            }
            const family = voucher.productFamily;
            if (family) {
                families.add(family);
            }
        });
        return Array.from(families);
    }, [hasContract, contractProducts]);
    const allPossibleFamilies = useMemo(() => {
        const families = [ProductFamily.AUXILIO];
        if (isPatEnabled) {
            families.push(ProductFamily.PAT);
        }
        return families;
    }, [isPatEnabled]);
    const availableFamilies = useMemo(() => {
        return allPossibleFamilies.filter(family => !contractedFamilies.includes(family));
    }, [allPossibleFamilies, contractedFamilies]);
    const shouldShowFamilyOption = useMemo(() => (family) => {
        return availableFamilies.includes(family);
    }, [availableFamilies]);
    const addFamiliesToExistingContract = useCallback(async (contractData) => {
        try {
            if (!existingContract?.id) {
                throw new Error('Contract ID is required');
            }
            if (!contractData.productFamilies || contractData.productFamilies.length === 0) {
                throw new Error('Product families are required');
            }
            const promise = addProductFamiliesToContract({
                voucherContractId: existingContract.id,
                productFamily: contractData.productFamilies,
            });
            requestRef.current = promise;
            await promise.unwrap();
            navigate('/beneficios', { state: { fromHiring: 'success' } });
        }
        catch {
            navigate('/beneficios', { state: { fromHiring: 'error' } });
        }
        finally {
            setIsSubmitting(false);
            requestRef.current = null;
        }
    }, [navigate, addProductFamiliesToContract, existingContract?.id]);
    const submitEmissorContract = useCallback(async (contractData) => {
        if (!contractData.document || !contractData.address) {
            throw new Error('Required contract data is missing');
        }
        const promise = createEmissorBenefitContract({
            document: contractData.document,
            address: contractData.address,
        });
        requestRef.current = promise;
        await promise.unwrap();
    }, [createEmissorBenefitContract]);
    const submitLegacyContract = useCallback(async (organizationId, contractData) => {
        if (!organizationId || !contractData.voucherPartnerId || !contractData.locationId) {
            throw new Error('Required contract data is missing');
        }
        const contractBody = {
            organizationId,
            voucherPartnerId: contractData.voucherPartnerId,
            locationId: contractData.locationId,
        };
        if (isPatEnabled && contractData.productFamilies && contractData.productFamilies.length > 0) {
            contractBody.productFamily = contractData.productFamilies;
        }
        const promise = createContract(contractBody);
        requestRef.current = promise;
        await promise.unwrap();
    }, [createContract, isPatEnabled]);
    const createNewContract = useCallback(async (organizationId, contractData) => {
        const productFamiliesForActivation = isPatEnabled && !isEmissorFlow && contractData.productFamilies?.length
            ? contractData.productFamilies
            : undefined;
        try {
            if (isEmissorFlow) {
                await submitEmissorContract(contractData);
            }
            else {
                await submitLegacyContract(organizationId, contractData);
            }
            navigate('/beneficios/activation-success-page', {
                state: { productFamilies: productFamiliesForActivation, isEmissorFlow },
            });
        }
        catch {
            navigate('/beneficios/activation-error-page', {
                state: { productFamilies: productFamiliesForActivation, isEmissorFlow },
            });
        }
        finally {
            setIsSubmitting(false);
            requestRef.current = null;
        }
    }, [navigate, isPatEnabled, isEmissorFlow, submitEmissorContract, submitLegacyContract]);
    const submitContract = useCallback(async ({ organizationId, contractData, isTermsAccepted }) => {
        if (!isTermsAccepted)
            return;
        setIsSubmitting(true);
        if (hasContract && existingContract?.id) {
            await addFamiliesToExistingContract(contractData);
        }
        else {
            await createNewContract(organizationId ?? '', contractData);
        }
    }, [hasContract, existingContract?.id, addFamiliesToExistingContract, createNewContract]);
    return {
        availableFamilies,
        shouldShowFamilyOption,
        isPatEnabled,
        isEmissorFlow,
        isLoadingFeatureFlags,
        hasExistingContract: hasContract,
        isLoadingContractData,
        submitContract,
        isSubmitting: isSubmitting || isCreating || isCreatingEmissor || isAddingFamilies,
    };
}
